/// <reference types="vite/client" />
import { createRouter, createWebHistory } from 'vue-router'
import { fetchAuthSession } from 'aws-amplify/auth'
import { useAuthStore } from './../stores/auth'
import { useLayoutStore } from './../stores/layout'
import { isRouteEnabled } from '../utils/navigationUtils'
import { isSubscriptionAccessDenied } from '../utils/subscriptionUtils'
import { billingService } from '../services/billing.service'
import { expireIdleSession, ensureSessionActivityTimestamp } from '../utils/sessionIdle'
const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: '/',
      redirect: '/dashboard'
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('../views/LoginView.vue')
    },
    {
      path: '/signup',
      name: 'signup',
      component: () => import('../views/SignupView.vue')
    },
    {
      path: '/profile',
      name: 'profile',
      component: () => import('../views/ProfileView.vue')
    },
    {
      path: '/token-inspector',
      name: 'token-inspector',
      component: () => import('../views/TokenInspectorView.vue')
    },
    {
      path: '/customers',
      name: 'customers',
      component: () => import('../views/CustomersView.vue')
    },
    {
      path: '/adminusers',
      name: 'adminusers',
      component: () => import('../views/AdminUsersView.vue')
    },
    {
      path: '/users',
      name: 'users',
      component: () => import('../views/AllUsersView.vue')
    },
    {
      path: '/devices/end',
      name: 'enddevices',
      component: () => import('../views/EndDevicesView.vue')
    },
    {
      path: '/devices/receiving',
      name: 'receivingdevices',
      component: () => import('../views/ReceivingDevicesView.vue')
    },
    {
      path: '/licenses/screens',
      name: 'androidscreens',
      component: () => import('../views/AndroidScreensView.vue')
    },
    {
      path: '/licenses/codes',
      name: 'promocodes',
      component: () => import('../views/PromoCodesView.vue')
    },
    {
      path: '/dashboard',
      name: 'dashboard',
      component: () => import('../views/DashboardView.vue')
    },
    {
      path: '/serialnumbers',
      name: 'serialnumbers',
      component: () => import('../views/SerialNumbersView.vue')
    },
    {
      path: '/billing',
      name: 'billing',
      component: () => import('../views/BillingView.vue')
    },
    {
      path: '/mongodb',
      name: 'mongodb',
      component: () => import('../views/ReportsView.vue')
    },
    {
      path: '/reports/display',
      name: 'reportsdisplay',
      component: () => import('../views/ReportsView.vue')
    },
    {
      path: '/reports/email',
      name: 'reportsemail',
      component: () => import('../views/SetEmailReportsView.vue')
    },
    {
      path: '/firebase/users',
      name: 'firebaseusers',
      component: () => import('../views/FirebaseUsersView.vue')
    },
    {
      path: '/firebase/customers',
      name: 'firebasecustomers',
      component: () => import('../views/FirebaseCustomersView.vue')
    },
    {
      path: '/firebase/dashboard',
      name: 'firebasedashboard',
      component: () => import('../views/FirebaseDashboardView.vue')
    },
    {
      path: '/mobileAccounts',
      name: 'mobileAccounts',
      component: () => import('../views/FirebaseUsersView.vue')
    },
    {
      path: '/mqttserver',
      name: 'mqttserver',
      component: () => import('../views/MqttServerView.vue')
    },
    {
      path: '/androidapps',
      name: 'androidapps',
      component: () => import('../views/AndroidAppsView.vue')
    },
    {
      path: '/invite/register',
      name: 'invite-register',
      component: () => import('../views/InviteRegistrationView.vue')
    },
    {
      path: '/forgot',
      name: 'forgot',
      component: () => import('../views/ForgotPasswordView.vue')
    }
  ]
})
async function refreshOrganisationSubscriptionStatus(authStore: ReturnType<typeof useAuthStore>) {
  const gate = await billingService.fetchSubscriptionGateState()
  if (gate != null) {
    authStore.subscriptionStatus = gate
    return authStore.subscriptionStatus
  }
  return authStore.subscriptionStatus
}
async function hasValidCognitoSession(): Promise<boolean> {
  try {
    const session = await fetchAuthSession()
    return !!session.tokens?.idToken
  } catch {
    return false
  }
}
router.beforeEach(async (to, from) => {
  const authStore = useAuthStore()
  const layoutStore = useLayoutStore()
  const publicRoutes = ['/login', '/signup', '/invite/register', '/forgot']
  const isPublicRoute =
    publicRoutes.includes(to.fullPath) ||
    publicRoutes.some((route) => to.fullPath.startsWith(route))
  if (isPublicRoute) {
    layoutStore.hideEveryDisplay()
  }
  if (isPublicRoute) {
    return true
  }
  if (await expireIdleSession()) {
    return { path: '/login' }
  }
  const isLoggedIn =
    authStore.authenticator?.authStatus === 'authenticated' || (await hasValidCognitoSession())
  if (!isLoggedIn) {
    return { path: '/login' }
  }
  ensureSessionActivityTimestamp()
  const subscriptionStatus = await refreshOrganisationSubscriptionStatus(authStore)
  if (isSubscriptionAccessDenied(subscriptionStatus) && to.path !== '/billing') {
    authStore.billingRenewalRedirectPending = true
    return { path: '/billing', query: { subscriptionRenewal: '1' } }
  }
  if (to.path === '/login' || to.path === '/signup') {
    return { path: '/dashboard' }
  }
  const enabled = isRouteEnabled(to.path)
  if (!enabled) {
    return { path: '/dashboard' }
  }
  return true
})
router.afterEach((to, from) => {
  const layoutStore = useLayoutStore()
  const authStore = useAuthStore()
  const publicRoutes = ['/login', '/signup', '/invite/register', '/forgot']
  const isPublicRoute =
    publicRoutes.includes(to.fullPath) ||
    publicRoutes.some((route) => to.fullPath.startsWith(route))
  if (isPublicRoute) {
    layoutStore.hideEveryDisplay()
    if (to.path === '/login') {
      authStore.finishLogout()
    }
  } else {
    layoutStore.showEveryDisplay()
  }
  if (window.innerWidth < 1200) {
    const collapsedItem = to.hash
    if (typeof collapsedItem === 'string' && collapsedItem.trim().length <= 0) {
      layoutStore.hideSideNav()
    }
  }
})
export default router
